import { TableColumnDef } from '@/types';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ControlLabel, Theme } from 'klayowebshared';
import { isEmpty } from 'lodash';
import React, { ChangeEvent, Component, SyntheticEvent } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { Utils } from '../../common/Utils';
import { Allowed_File_Extensions, CHECKLIST_TYPE } from '../../constants';
import { IChecklistOption } from '../../interfaces/CheckLists';
import { RootState } from '../../interfaces/common';
import { SELECTED_FILE_TYPE } from '../../interfaces/Practical';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import AttachIcon from '../../resources/images/icons-attachment.svg';
import {
  onCloseChecklistPreviewDialog,
  onOpenChecklistPreviewDialog,
} from '../../store/checklist.slice';
import { KLAYO_COLORS } from '../../themes';
import { Button } from '../Button';
import { DragDropFileUpload } from '../DragDropFileUpload';
import { itemSx } from '../FileUploadDialog';
import { GroupByMultiSelector } from '../selectors/GroupByMultiSelector';
import { CheckListsTable } from './CheckListsTable';
import { DocumentTable } from './DocumentTable';

/** Document item in the list */
interface DocumentItem {
  id?: string | number;
  name?: string;
  isDeleted?: boolean;
  pendingDelete?: boolean;
  documentId?: string;
  documentName?: string;
  [key: string]: unknown;
}

/** Checklist answer from store */
interface ChecklistAnswer {
  questionId: string;
  [key: string]: unknown;
}

/** Checklist answers state from Redux */
interface ChecklistAnswersState {
  checklistId?: string;
  answers?: ChecklistAnswer[];
}

/** Checklist section */
interface ChecklistSection {
  questions?: Array<{ id: string; answer?: ChecklistAnswer; [key: string]: unknown }>;
  [key: string]: unknown;
}

/** Selected checklist with sections */

/** Props for DocumentList component */
interface DocumentListOwnProps<
  IDocumentItem extends DocumentItem = DocumentItem,
> extends WithLocaleProps {
  theme?: typeof Theme;
  documents?: IDocumentItem[];
  label?: string;
  files?: File[];
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  maxFileSize?: number;
  errorMimeType?: string;
  errorFileSize?: string;
  onFilesChange?: (e: ChangeEvent<HTMLInputElement>, files: File[]) => void;
  showDeleted?: boolean;
  disabled?: boolean;
  isShowInlineFiles?: boolean;
  isShowFileType?: boolean;
  onChange?: (event: SyntheticEvent | null, selected: IChecklistOption[]) => void;
  selectedChecklist?: IChecklistOption[];
  onDeleteChecklist?: (event: SyntheticEvent, id: string) => void;
  checklistsUploaded?: IChecklistOption[];
  checklists?: IChecklistOption[];
  fileType?: string;
  setSelectedFileType?: (value: SELECTED_FILE_TYPE) => void;
  onViewDocument?: (event: SyntheticEvent, doc: IDocumentItem) => void;
  onDeleteDocument?: (event: SyntheticEvent, doc: IDocumentItem) => void;
  onUpdateChecklistData?: (updated: IChecklistOption[]) => void;
  onDeleteExistedChecklist?: (event: SyntheticEvent, assignmentId: string) => void;
  checklistType?: CHECKLIST_TYPE;
  isRequireComplete?: boolean;
  allowedMimeTypesLabel?: string;
  documentColumns?: TableColumnDef[];
  documentRowHeight?: number;
}

/** State for DocumentList component */
interface DocumentListState {
  showUploadComponent: boolean;
  actionDocument: DocumentItem | null;
  showDeleted: boolean;
  selectedFileType: string;
  checkLists: IChecklistOption[];
  error: string | null;
  isInvalidChecklists: boolean;
}

const mapState = (state: RootState) => ({
  checklistAnswers: state.checklist.checklistAnswers as unknown as ChecklistAnswersState | null,
});

const mapDispatch = {
  onOpenChecklistPreviewDialog,
  onCloseChecklistPreviewDialog,
};

const withRedux = connect(mapState, mapDispatch);

type ReduxProps = ConnectedProps<typeof withRedux>;
type DocumentListProps<IDocumentItem extends DocumentItem = DocumentItem> =
  DocumentListOwnProps<IDocumentItem> & ReduxProps;

class DocumentList<IDocumentItem extends DocumentItem = DocumentItem> extends Component<
  DocumentListProps<IDocumentItem>,
  DocumentListState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    label: SELECTED_FILE_TYPE.DOCUMENT,
    isShowFileType: true,
    isShowInlineFiles: true,
    allowedExtensions: Allowed_File_Extensions,
  };

  constructor(props: DocumentListProps<IDocumentItem>) {
    super(props);

    this.state = {
      showUploadComponent: false,
      actionDocument: null,
      showDeleted: false,
      selectedFileType: props.fileType ?? SELECTED_FILE_TYPE.DOCUMENT,
      checkLists: [],
      error: null,
      isInvalidChecklists: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<DocumentListProps<IDocumentItem>>): void {
    if (
      prevProps.checklistAnswers !== this.props.checklistAnswers &&
      this.props.checklistAnswers &&
      this.props.checklistAnswers.checklistId
    ) {
      const { onUpdateChecklistData, selectedChecklist } = this.props;
      const saved = this.props.checklistAnswers;
      if (Array.isArray(selectedChecklist) && onUpdateChecklistData) {
        const updated = selectedChecklist.map((c) => {
          if (c.id !== saved.checklistId) return c;

          const newAnswers = saved.answers || [];
          const normalizedSections = (c.sections || []).map((section) => ({
            ...section,
            questions: (section.questions || []).map((question) => ({
              ...question,
              answer: newAnswers.find((a) => a.questionId === question.id),
            })),
          }));

          return {
            ...c,
            answers: newAnswers,
            sections: normalizedSections,
          };
        });
        onUpdateChecklistData(updated);
      }
    }

    if (prevProps.selectedChecklist !== this.props.selectedChecklist) {
      this.checkValidSelectedChecklists();
    }
  }

  componentDidMount(): void {
    if (this.props.isShowFileType && !!this.context?.organization?.enabledFeatures?.Training) {
      this.onGetAllCheckLists();
    }
  }

  onGetAllCheckLists(): void {
    axiosClient
      .get<{ checklists: { data: IChecklistOption[] } }>(`/Checklist?pageSize=1000`)
      .then((response) => {
        this.setState({ checkLists: response.data.checklists.data });
      });
  }

  onShowUploadComponent(_e: SyntheticEvent): void {
    this.setState({ showUploadComponent: true });
  }

  onViewDocument(e: SyntheticEvent, doc: IDocumentItem): void {
    const { onViewDocument } = this.props;
    if (onViewDocument) onViewDocument(e, doc);
  }

  onDeleteDocument(e: SyntheticEvent, doc: IDocumentItem): void {
    const { onDeleteDocument } = this.props;
    if (onDeleteDocument) onDeleteDocument(e, doc);
  }

  handleSelectFileTypeChange(_e: React.MouseEvent<HTMLElement>, value: SELECTED_FILE_TYPE): void {
    if (!value) return;
    const { setSelectedFileType } = this.props;
    if (setSelectedFileType) setSelectedFileType(value);
    this.setState({ selectedFileType: value });
  }

  onOpenChecklistPreviewDialog(checklist: any): void {
    const { onOpenChecklistPreviewDialog, checklistType } = this.props;

    if (onOpenChecklistPreviewDialog) {
      onOpenChecklistPreviewDialog({
        id: checklist.id,
        isEditMode: true,
        checklistData: checklist,
        checklistType: checklistType,
      });
    }
  }

  onViewExistedChecklist(checklist: IChecklistOption): void {
    const { onOpenChecklistPreviewDialog, checklistType } = this.props;

    if (onOpenChecklistPreviewDialog) {
      onOpenChecklistPreviewDialog({
        isEditMode: false,
        disabled: true,
        checklistData: checklist,
        checklistType: checklistType,
      });
    }
  }

  onDeleteExistedChecklist(e: SyntheticEvent, checklist: IChecklistOption): void {
    if (checklist.assignmentId) {
      this.props.onDeleteExistedChecklist?.(e, checklist.assignmentId);
    }
  }

  checkValidSelectedChecklists(): void {
    const { selectedChecklist, isRequireComplete } = this.props;

    if (isEmpty(selectedChecklist) || !isRequireComplete) {
      return this.setState({
        isInvalidChecklists: false,
      });
    }

    const isValid = selectedChecklist?.every((checklist) => {
      const isComplete = Utils.checkAllAnswersComplete(checklist?.answers as any);

      return isComplete;
    });

    this.setState({
      isInvalidChecklists: !isValid,
    });
  }

  render() {
    const {
      theme,
      documents,
      label,
      files,
      allowedMimeTypes,
      allowedExtensions,
      maxFileSize,
      errorMimeType,
      errorFileSize,
      onFilesChange,
      showDeleted,
      disabled,
      isShowInlineFiles,
      isShowFileType,
      onChange,
      selectedChecklist,
      onDeleteChecklist,
      checklistsUploaded,
      checklists,
      allowedMimeTypesLabel,
      documentColumns,
      documentRowHeight = 48,
    } = this.props;

    const { showUploadComponent, checkLists, isInvalidChecklists } = this.state;
    const docs = documents
      ? showDeleted
        ? documents
        : documents.filter((d: DocumentItem) => !d.isDeleted && !d.pendingDelete)
      : null;
    const hasDocs = docs && docs.length > 0;
    const hasChecklist = !isEmpty(checklists);
    const hasFiles =
      files && Object.entries(files).filter(([_, value]) => Boolean(value)).length > 0;
    const hasSelectChecklists = !isEmpty(selectedChecklist);

    return (
      <Stack
        direction={'column'}
        spacing={hasDocs || hasChecklist ? 2 : 0}
        className='klayo-doclist'
        sx={{ width: '100%' }}
      >
        <ControlLabel text={label} />
        <Stack spacing={2}>
          {(hasDocs || hasChecklist) && (
            <Stack>
              {hasDocs && (
                <>
                  <DocumentTable<DocumentItem>
                    theme={theme}
                    showHeader={false}
                    pagination={false}
                    showFooter={false}
                    hideFirstLastBorder={true}
                    dense={false}
                    paper={false}
                    rows={docs}
                    rowHeight={documentRowHeight}
                    sx={{
                      borderBottom: '1px solid var(--grey-gr-200)',
                      borderRadius: '0!important',
                    }}
                    columns={
                      documentColumns || [
                        { type: DocumentTable.columns.name },
                        ...(disabled ? [] : [{ type: DocumentTable.columns.delete }]),
                      ]
                    }
                    onDeleteDocument={this.onDeleteDocument.bind(this) as any}
                    onDocumentSelect={this.onViewDocument.bind(this) as any}
                  />
                </>
              )}
              {hasChecklist && !!this.context?.organization?.enabledFeatures?.Training && (
                <>
                  <CheckListsTable<IChecklistOption>
                    theme={theme}
                    showHeader={false}
                    pagination={false}
                    showFooter={false}
                    hideFirstLastBorder={true}
                    dense={false}
                    rows={checklists}
                    columns={[
                      { type: CheckListsTable.columns.checklistName },
                      { type: CheckListsTable.columns.status },
                      ...(disabled ? [] : [{ type: CheckListsTable.columns.delete }]),
                    ]}
                    paper={false}
                    rowHeight={48}
                    onDeleteCheckListAction={this.onDeleteExistedChecklist.bind(this)}
                    onChecklistClick={this.onViewExistedChecklist.bind(this)}
                    translate={this.props.translate}
                  />
                </>
              )}
            </Stack>
          )}

          {!showUploadComponent &&
            (hasDocs || hasChecklist) &&
            (!hasFiles || !hasSelectChecklists) && (
              <div>
                <Button
                  size='md'
                  startIcon={<AttachIcon />}
                  onClick={this.onShowUploadComponent.bind(this)}
                  disabled={disabled}
                  label={this.props.translate('fileUpload.addAnAttachment')}
                />
              </div>
            )}

          {(showUploadComponent ||
            (!hasDocs && !hasChecklist) ||
            hasFiles ||
            hasSelectChecklists) && (
            <>
              {isShowFileType && !!this.context?.organization?.enabledFeatures?.Training && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px !important',
                      fontFamily: 'Inter !important',
                      fontWeight: '500 !important',
                      marginLeft: '15px',
                    }}
                  >
                    {this.props.translate('fileUpload.fileType')}
                  </Typography>
                  <ToggleButtonGroup
                    value={this.state.selectedFileType}
                    exclusive
                    onChange={(e, value) => this.handleSelectFileTypeChange(e, value)}
                    sx={{ gap: 2, marginTop: '10px' }}
                  >
                    <ToggleButton value={SELECTED_FILE_TYPE.DOCUMENT} sx={itemSx}>
                      {this.props.translate('fileUpload.document')}
                    </ToggleButton>
                    <ToggleButton value={SELECTED_FILE_TYPE.CHECKLIST} sx={itemSx}>
                      {this.props.translate('fileUpload.checklist')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              {this.state.selectedFileType === SELECTED_FILE_TYPE.DOCUMENT ? (
                <DragDropFileUpload
                  label={null}
                  files={files}
                  accept='csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
                  onChange={onFilesChange}
                  allowedMimeTypes={allowedMimeTypes}
                  allowedExtensions={allowedExtensions}
                  maxFileSize={maxFileSize}
                  errorMimeType={errorMimeType}
                  errorFileSize={errorFileSize}
                  isShowInlineFiles={isShowInlineFiles}
                  theme={theme}
                  allowedMimeTypesLabel={allowedMimeTypesLabel}
                  disabled={disabled}
                />
              ) : (
                <>
                  <GroupByMultiSelector<IChecklistOption>
                    value={selectedChecklist as any}
                    options={
                      checkLists.filter(
                        (c: IChecklistOption) => !checklistsUploaded?.some((cu) => cu.id === c.id)
                      ) || []
                    }
                    label={this.props.translate('fileUpload.checklist')}
                    placeHolder={this.props.translate('fileUpload.searchAndSelect')}
                    validationText={this.props.translate(
                      'addEditPractical.validPracticalCompetency'
                    )}
                    noOptionsText={this.props.translate('common.noSearchResults')}
                    getOptionLabel={(o: IChecklistOption) => o.name || ''}
                    validationMethod={() => true}
                    onChange={onChange}
                    getOptionId={(o: IChecklistOption) => o.id}
                    isShowValue={false}
                    isValidateOnMount={true}
                    onBlur={() => {}}
                    onSearch={() => {}}
                    disabled={false}
                    groupBy={(
                      o: IChecklistOption & {
                        group?: string;
                      }
                    ) => o.group || ''}
                    isOptionEqualToValue={(option: IChecklistOption, value: IChecklistOption) =>
                      option.id === value.id
                    }
                    style={{ maxHeight: '300px' }}
                  />
                  {selectedChecklist && selectedChecklist?.length > 0 && (
                    <CheckListsTable<IChecklistOption>
                      theme={theme}
                      showHeader={false}
                      pagination={false}
                      showFooter={false}
                      hideFirstLastBorder={true}
                      dense={false}
                      rows={selectedChecklist as any}
                      columns={[
                        { type: CheckListsTable.columns.checklistName },
                        { type: CheckListsTable.columns.status },
                        ...(disabled ? [] : [{ type: CheckListsTable.columns.delete }]),
                      ]}
                      paper={false}
                      rowHeight={48}
                      onDeleteCheckListAction={(e: SyntheticEvent, row) =>
                        onDeleteChecklist?.(e, row.id || '')
                      }
                      onChecklistClick={(row) => this.onOpenChecklistPreviewDialog(row)}
                      translate={this.props.translate}
                    />
                  )}
                  {isInvalidChecklists && (
                    <Box
                      component={'small'}
                      padding={'0 14px'}
                      fontSize={14}
                      color={KLAYO_COLORS.RedWarning}
                    >
                      {this.props.translate('fileUpload.pleaseCompleteAllChecklists')}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    );
  }
}

const DocumentListWithRedux = withRedux(DocumentList);
const DocumentListWithLocale = withLocale(DocumentListWithRedux);

// Type assertion to preserve generic type parameter through Redux connect HOC
type DocumentListComponent = <T extends DocumentItem = DocumentItem>(
  props: DocumentListOwnProps<T>
) => React.ReactElement | null;

const DocumentListExport = DocumentListWithLocale as unknown as DocumentListComponent;

export { DocumentListExport as DocumentList };
export type { DocumentItem };
export default DocumentListExport;
